// src/stores/ClientSiteStore.js - Zustand Store for Client Sites
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useClientSiteStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Main site data - single source of truth
    sites: [],
    sitesLoading: false,

    // Selected site state (for site details/editing)
    selectedSite: null,
    selectedSiteLoading: false,

    // Simple operation states
    savingDraft: false,
    publishing: false,

    // Connection state
    isConnected: false,

    // Internal flags
    _initialized: false,

    // ================================
    // EDITING STATE (Real-time Preview)
    // ================================

    // Editing session state - single source of truth for site editing
    editing: {
      siteId: null,              // ID of site currently being edited
      draft: null,               // Working copy of site content (draft_configuration)
      isDirty: false,            // Has unsaved changes to draft
      lastAutoSave: null         // Timestamp of last auto-save
    },

    // Internal auto-save timer
    _autoSaveTimer: null,

    // ================================
    // SOCKET OPERATIONS
    // ================================

    /**
     * Request all client sites for the business
     * @param {object} options - Additional options (force, etc.)
     */
    loadSites: (options = {}) => {
      const { sitesLoading } = get();

      if (sitesLoading && !options.force) {
        console.log('ClientSiteStore: Already loading sites, skipping');
        return;
      }

      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ ClientSiteStore: Socket service not initialized');
        return;
      }

      console.log('ClientSiteStore: Loading sites...');

      set({
        sitesLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_CLIENT_SITES, {});
    },

    /**
     * Request details for a specific site
     * @param {string} siteId - Site ID to load
     */
    loadSiteDetails: (siteId) => {
      if (!siteId) {
        console.error('ClientSiteStore: loadSiteDetails requires siteId');
        return;
      }

      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ ClientSiteStore: Socket service not initialized');
        return;
      }

      console.log('ClientSiteStore: Loading site details:', siteId);

      set({
        selectedSiteLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_SITE_DETAILS, {
        site_id: siteId
      });
    },

    /**
     * Update draft content for a site (LEGACY - kept for compatibility)
     * @deprecated Use startEditing/updateEditingDraft instead
     * @param {string} siteId - Site ID
     * @param {object} draftContent - New draft content
     */
    updateDraft: (siteId, draftContent) => {
      console.log('⚠️ ClientSiteStore.updateDraft (legacy) called - consider using updateEditingDraft');

      if (!siteId || !draftContent) {
        console.error('❌ ClientSiteStore: updateDraft requires siteId and draftContent');
        return;
      }

      const { socketManager } = get()._getSocketManager();

      if (!socketManager?.universalHandler) {
        console.error('❌ ClientSiteStore: Socket service not initialized');
        return;
      }

      set({
        savingDraft: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_SITE_DRAFT, {
        site_id: siteId,
        draft_content: draftContent
      });
    },

    /**
     * Publish a site (copy draft to live)
     * @param {string} siteId - Site ID to publish
     */
    publishSite: (siteId) => {
      if (!siteId) {
        console.error('ClientSiteStore: publishSite requires siteId');
        return;
      }

      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ ClientSiteStore: Socket service not initialized');
        return;
      }

      console.log('ClientSiteStore: Publishing site:', siteId);

      set({
        publishing: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.PUBLISH_SITE, {
        site_id: siteId
      });
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get site by ID from the sites array
     * @param {string} siteId - Site ID to find
     * @returns {object|null} - Site object or null
     */
    getSiteById: (siteId) => {
      const { sites } = get();
      return sites.find(site => site.id === siteId) || null;
    },

    /**
     * Clear selected site
     */
    clearSelectedSite: () => {
      set({
        selectedSite: null,
        selectedSiteLoading: false
      });
    },

    /**
     * Refresh sites (force reload)
     */
    refreshSites: () => {
      get().loadSites({ force: true });
    },

    // ================================
    // EDITING ACTIONS (Real-time Preview)
    // ================================

    /**
     * Start editing a site - initializes editing session
     * @param {string} siteId - Site ID to edit
     */
    startEditing: (siteId) => {
      const { selectedSite, sites } = get();

      // Get the site data
      const site = selectedSite?.id === siteId
        ? selectedSite
        : sites.find(s => s.id === siteId);

      if (!site) {
        console.error('ClientSiteStore: Cannot start editing - site not found:', siteId);
        return;
      }

      console.log('🎬 ClientSiteStore: Starting editing session for site:', site.site_name);

      // Initialize editing state from draft_configuration
      set({
        editing: {
          siteId: siteId,
          draft: { ...site.draft_configuration }, // Make a copy of the draft
          isDirty: false,
          lastAutoSave: Date.now()
        }
      });
    },

    /**
     * Update editing draft configuration (triggers auto-save)
     * @param {object} updates - Updates to merge into draft
     */
    updateEditingDraft: (updates) => {
      const { editing } = get();

      if (!editing.siteId) {
        console.error('ClientSiteStore: Cannot update draft - no editing session active');
        return;
      }

      console.log('✏️ ClientSiteStore: Updating draft with changes');

      // Deep merge updates into draft
      set(state => ({
        editing: {
          ...state.editing,
          draft: {
            ...state.editing.draft,
            ...updates
          },
          isDirty: true
        }
      }));

      // Schedule auto-save
      get()._scheduleAutoSave();
    },

    /**
     * Stop editing session - clear editing state
     */
    stopEditing: () => {
      const { _autoSaveTimer } = get();

      // Clear any pending auto-save
      if (_autoSaveTimer) {
        clearTimeout(_autoSaveTimer);
      }

      console.log('🛑 ClientSiteStore: Stopping editing session');

      set({
        editing: {
          siteId: null,
          draft: null,
          isDirty: false,
          lastAutoSave: null
        },
        _autoSaveTimer: null
      });
    },

    /**
     * Internal: Schedule auto-save with debounce
     * @private
     */
    _scheduleAutoSave: () => {
      const store = get();

      // Clear existing timer
      if (store._autoSaveTimer) {
        clearTimeout(store._autoSaveTimer);
      }

      // Schedule new auto-save after 500ms
      const timer = setTimeout(() => {
        const { editing } = get();

        if (editing.isDirty && editing.siteId && editing.draft) {
          console.log('💾 ClientSiteStore: Auto-saving draft changes...');

          const { socketManager } = get()._getSocketManager();
          if (!socketManager?.universalHandler) {
            console.error('❌ ClientSiteStore: Socket service not initialized');
            return;
          }

          set({ savingDraft: true });

          socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_SITE_DRAFT, {
            site_id: editing.siteId,
            draft_content: editing.draft
          });

          // Mark as saved (but keep the draft as-is)
          set(state => ({
            editing: {
              ...state.editing,
              isDirty: false,
              lastAutoSave: Date.now()
            }
          }));
        }
      }, 500);

      set({ _autoSaveTimer: timer });
    },

    // ================================
    // INTERNAL METHODS
    // ================================

    /**
     * Get socket manager reference (set during initialization)
     * @private
     */
    _getSocketManager: () => {
      const socketManager = get()._socketManager;
      return { socketManager };
    },

    /**
     * Initialize store with socket manager and auto-load data
     * @private
     */
    _initialize: (socketManager) => {
      const state = get();

      console.log('ClientSiteStore: Initializing with socket manager');

      if (typeof window !== 'undefined') {
        window.__clientSiteSocketManager = socketManager;
      }

      set({
        _socketManager: socketManager,
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load sites if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('ClientSiteStore: Auto-loading sites (first time)');
        state.loadSites();
      }
    },

    // ================================
    // SOCKET RESPONSE HANDLERS
    // ================================

    /**
     * Handle client_sites_loaded response
     * Success handlers receive the data directly (unwrapped by UniversalResponseHandler)
     * @private
     */
    _handleSitesLoaded: (data) => {
      console.log('ClientSiteStore: Sites loaded:', data);

      const sitesData = data || [];

      set({
        sites: sitesData,
        sitesLoading: false
      });

      // Auto-select first site if we don't have one selected
      // Note: The sites list already includes full configuration data,
      // so we can set selectedSite directly without another API call
      const { selectedSite } = get();
      if (!selectedSite && sitesData.length > 0) {
        const firstSite = sitesData[0];
        console.log('ClientSiteStore: Auto-selecting first site:', firstSite.site_slug);
        set({
          selectedSite: firstSite
        });
      }
    },

    /**
     * Handle site_details_loaded response
     * Success handlers receive the data directly (unwrapped by UniversalResponseHandler)
     * @private
     */
    _handleSiteDetailsLoaded: (data) => {
      console.log('ClientSiteStore: Site details loaded:', data);

      set({
        selectedSite: data,
        selectedSiteLoading: false
      });
    },

    /**
     * Handle site_draft_updated response
     * Success handlers receive the data directly (unwrapped by UniversalResponseHandler)
     * @private
     */
    _handleDraftUpdated: (data) => {
      console.log('✅ ClientSiteStore: Draft saved to server');

      // Update selectedSite and sites array with server data
      // BUT DO NOT TOUCH editing.draft (that's the user's scratch paper!)
      set({
        selectedSite: data,
        savingDraft: false
      });

      // Also update in sites array
      const { sites } = get();
      const updatedSites = sites.map(site =>
        site.id === data.id ? data : site
      );
      set({ sites: updatedSites });

      console.log('📁 ClientSiteStore: Server copy updated (editing.draft untouched)');
    },

    /**
     * Handle site_published response
     * Success handlers receive the data directly (unwrapped by UniversalResponseHandler)
     * @private
     */
    _handleSitePublished: (data) => {
      console.log('ClientSiteStore: Site published:', data);

      set({
        selectedSite: data,
        publishing: false
      });

      // Also update in sites array
      const { sites } = get();
      const updatedSites = sites.map(site =>
        site.id === data.id ? data : site
      );
      set({ sites: updatedSites });
    },

    /**
     * Handle error responses
     * @private
     */
    _handleError: (response) => {
      console.error('ClientSiteStore: Error:', response);

      // Reset all loading states
      set({
        sitesLoading: false,
        selectedSiteLoading: false,
        savingDraft: false,
        publishing: false
      });
    }
  }))
);

/**
 * Initialize socket event listeners for ClientSiteStore
 * Called once at app startup by StoreInitializer
 *
 * @param {object} socketManager - The SocketManager instance
 */
export const initializeClientSiteStoreSocketListeners = async (socketManager) => {
  if (!socketManager?.universalHandler) {
    console.error('❌ ClientSiteStore: Cannot initialize - socket manager not available');
    return;
  }

  const store = useClientSiteStore.getState();

  // Check if already initialized
  if (store._initialized) {
    console.log('⚠️ ClientSiteStore: Already initialized, skipping');
    return;
  }

  console.log('🔌 ClientSiteStore: Registering socket event listeners');

  // Register response handlers
  socketManager.universalHandler.on(SOCKET_EVENTS.CLIENT_SITES_LOADED, store._handleSitesLoaded);
  socketManager.universalHandler.on(SOCKET_EVENTS.SITE_DETAILS_LOADED, store._handleSiteDetailsLoaded);
  socketManager.universalHandler.on(SOCKET_EVENTS.SITE_DRAFT_UPDATED, store._handleDraftUpdated);
  socketManager.universalHandler.on(SOCKET_EVENTS.SITE_PUBLISHED, store._handleSitePublished);

  // Register error handlers
  socketManager.universalHandler.on(SOCKET_EVENTS.GET_CLIENT_SITES_FAILED, store._handleError);
  socketManager.universalHandler.on(SOCKET_EVENTS.GET_SITE_DETAILS_FAILED, store._handleError);
  socketManager.universalHandler.on(SOCKET_EVENTS.UPDATE_SITE_DRAFT_FAILED, store._handleError);
  socketManager.universalHandler.on(SOCKET_EVENTS.PUBLISH_SITE_FAILED, store._handleError);

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useClientSiteStore.setState({
        sites: [],
        selectedSite: null,
        _initialized: false,
        isConnected: false
      });
    });
  }

  // Initialize the store (this will auto-load sites if authenticated)
  store._initialize(socketManager);

  console.log('✅ ClientSiteStore: Socket listeners registered successfully');
};

export default useClientSiteStore;
